import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

/** Matches backend: Dealer, Importer, AuctionHouse, Individual */
export const SUPPLIER_TYPE_VALUES = ["dealer", "importer", "auction-house", "individual"] as const;
export type SupplierType = (typeof SUPPLIER_TYPE_VALUES)[number];

export const SUPPLIER_TYPE_LABELS: Record<SupplierType, string> = {
  dealer: "Dealer",
  importer: "Importer",
  "auction-house": "Auction House",
  individual: "Individual",
};

export const DEFAULT_SUPPLIER_TYPE: SupplierType = "dealer";

/** Normalize API / legacy UI strings to backend enum values. */
export function normalizeSupplierType(raw: string | null | undefined): SupplierType {
  const s = (raw ?? "").trim();
  if (!s) return DEFAULT_SUPPLIER_TYPE;
  const key = s
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/[\s_]+/g, "-")
    .toLowerCase();
  if (key === "dealer") return "dealer";
  if (key === "importer") return "importer";
  if (key === "auction-house" || key === "auctionhouse") return "auction-house";
  if (key === "individual") return "individual";
  return DEFAULT_SUPPLIER_TYPE;
}

export function supplierTypeLabel(type: string | null | undefined): string {
  const n = normalizeSupplierType(type);
  return SUPPLIER_TYPE_LABELS[n];
}

export interface SupplierRecord {
  id: string;
  name: string;
  fatherName: string;
  cnic: string;
  contactPerson: string;
  country: number | null;
  countryName: string;
  state: number | null;
  stateName: string;
  city: string;
  postalCode: string;
  addressLine1: string;
  addressLine2: string;
  /** Combined address_line_1 + address_line_2 for search/display convenience. */
  address: string;
  phone: string;
  email: string;
  type: SupplierType;
  status: "Active" | "Inactive";
  createdAt: string;
  updatedAt: string;
}

export type SupplierDraft = Pick<
  SupplierRecord,
  "name" | "fatherName" | "cnic" | "contactPerson" | "phone" | "email" | "city" | "type" | "status"
> & {
  country: number | null;
  state: number | null;
  postalCode: string;
  addressLine1: string;
  addressLine2: string;
};

const LS_KEY = "inventory.suppliers.v2";

type SupplierApiRecord = {
  id?: number | string;
  name: string;
  father_name?: string | null;
  cnic?: string | null;
  contact_person?: string | null;
  country?: number | string | null;
  country_name?: string | null;
  state?: number | string | null;
  state_name?: string | null;
  city?: string | null;
  postal_code?: string | null;
  address_line_1?: string | null;
  address_line_2?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  type?: string | null;
  /** Backend may return boolean; legacy APIs may return strings. */
  status?: boolean | string | number | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type UpsertSupplierApiResponse = {
  message?: string;
  data?: SupplierApiRecord;
};

type DeleteSupplierApiResponse = {
  message?: string;
};

type UpsertSupplierResult = {
  record: SupplierRecord;
  message: string;
};

type DeleteSupplierResult = {
  success: boolean;
  message: string;
};

/** Map API / boolean status to UI labels. */
function normalizeStatusFromApi(raw: unknown): "Active" | "Inactive" {
  if (raw === true || raw === 1) return "Active";
  if (raw === false || raw === 0) return "Inactive";
  if (typeof raw === "string") {
    const v = raw.trim().toLowerCase();
    if (v === "inactive" || v === "false" || v === "0") return "Inactive";
    if (v === "active" || v === "true" || v === "1") return "Active";
  }
  return "Active";
}

function supplierStatusToBoolean(status: SupplierDraft["status"]): boolean {
  return status === "Active";
}

export const emptySupplierDraft = (): SupplierDraft => ({
  name: "",
  fatherName: "",
  cnic: "",
  contactPerson: "",
  phone: "",
  email: "",
  country: null,
  state: null,
  city: "",
  postalCode: "",
  addressLine1: "",
  addressLine2: "",
  type: DEFAULT_SUPPLIER_TYPE,
  status: "Active",
});

export function formatSupplierDisplayName(
  supplier: Pick<SupplierRecord, "name" | "fatherName">,
): string {
  const name = supplier.name.trim();
  const father = supplier.fatherName.trim();
  if (!name) return father || "—";
  if (!father) return name;
  return `${name} (S/O ${father})`;
}

export function supplierErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  return "Something went wrong. Please try again.";
}

function dedupeById(items: SupplierRecord[]): SupplierRecord[] {
  const byId = new Map<ID, SupplierRecord>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function extractRowsFromResponse(body: unknown): SupplierApiRecord[] {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;

  const fromBlock = (block: unknown): SupplierApiRecord[] | null => {
    if (Array.isArray(block)) return block as SupplierApiRecord[];
    if (block && typeof block === "object" && !Array.isArray(block)) {
      const inner = (block as Record<string, unknown>).data;
      if (Array.isArray(inner)) return inner as SupplierApiRecord[];
    }
    return null;
  };

  for (const key of ["data", "suppliers", "items"]) {
    const hit = fromBlock(root[key]);
    if (hit) return hit;
  }
  return [];
}

function mapApiRecord(record: SupplierApiRecord): SupplierRecord {
  const created = record.created_at || new Date().toISOString();
  const line1 = record.address_line_1 ?? record.address ?? "";
  const line2 = record.address_line_2 ?? "";
  const combined = [line1, line2].map((s) => s.trim()).filter(Boolean).join(", ");
  return {
    id: String(record.id ?? ""),
    name: record.name,
    fatherName: record.father_name ?? "",
    cnic: record.cnic ?? "",
    contactPerson: record.contact_person ?? "",
    country: record.country != null && record.country !== "" ? Number(record.country) : null,
    countryName: record.country_name ?? "",
    state: record.state != null && record.state !== "" ? Number(record.state) : null,
    stateName: record.state_name ?? "",
    city: record.city ?? "",
    postalCode: record.postal_code ?? "",
    addressLine1: line1,
    addressLine2: line2,
    address: combined,
    phone: record.phone ?? "",
    email: record.email ?? "",
    type: normalizeSupplierType(record.type),
    status: normalizeStatusFromApi(record.status),
    createdAt: created,
    updatedAt: record.updated_at ?? created,
  };
}

export function normalizeSupplierDraft(draft: SupplierDraft): SupplierDraft {
  return {
    name: draft.name.trim(),
    fatherName: draft.fatherName?.trim() ?? "",
    cnic: draft.cnic?.trim() ?? "",
    contactPerson: draft.contactPerson?.trim() ?? "",
    phone: draft.phone?.trim() ?? "",
    email: draft.email?.trim() ?? "",
    country: draft.country ?? null,
    state: draft.state ?? null,
    city: draft.city?.trim() ?? "",
    postalCode: draft.postalCode?.trim() ?? "",
    addressLine1: draft.addressLine1?.trim() ?? "",
    addressLine2: draft.addressLine2?.trim() ?? "",
    type: normalizeSupplierType(draft.type),
    status: draft.status === "Inactive" ? "Inactive" : "Active",
  };
}

export type SupplierDraftValidation = {
  valid: boolean;
  message?: string;
};

export function validateSupplierDraft(draft: SupplierDraft): SupplierDraftValidation {
  const n = normalizeSupplierDraft(draft);
  if (!n.name) return { valid: false, message: "Supplier name is required." };
  // Father name and CNIC are optional
  if (!n.phone) return { valid: false, message: "Contact number is required." };
  if (!n.country) return { valid: false, message: "Country is required." };
  if (!n.state) return { valid: false, message: "State is required." };
  if (!n.city) return { valid: false, message: "City is required." };
  return { valid: true };
}

export function isSupplierDraftComplete(draft: SupplierDraft): boolean {
  return validateSupplierDraft(draft).valid;
}

function buildApiPayload(draft: SupplierDraft) {
  const n = normalizeSupplierDraft(draft);
  return {
    name: n.name,
    father_name: n.fatherName || null,
    cnic: n.cnic || null,
    contact_person: n.contactPerson || null,
    phone: n.phone || null,
    email: n.email || null,
    country: n.country,
    state: n.state,
    city: n.city || null,
    postal_code: n.postalCode || null,
    address_line_1: n.addressLine1 || null,
    address_line_2: n.addressLine2 || null,
    type: n.type,
    status: supplierStatusToBoolean(n.status),
  };
}

function load(): SupplierRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as SupplierRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: SupplierRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

export function listSuppliers(): SupplierRecord[] {
  return load()
    .map((s) => ({
      ...s,
      type: normalizeSupplierType(s.type),
      status: normalizeStatusFromApi(s.status),
    }))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function getSupplierById(id: ID): SupplierRecord | undefined {
  return load().find((s) => s.id === id);
}

export async function syncSuppliersFromApi() {
  try {
    const response = await api.get("/api/v1/suppliers", {
      params: { page: 1, per_page: 200 },
    });
    const rows = extractRowsFromResponse(response.data);
    const records = rows.map(mapApiRecord).filter((r) => Boolean(r.id));
    save(records);
    return listSuppliers();
  } catch (error) {
    if (!(isAxiosError(error) && error.response?.status === 403)) {
      console.error("[Suppliers] syncSuppliersFromApi failed", error);
    }
    throw error;
  }
}

export async function createSupplier(draft: SupplierDraft): Promise<UpsertSupplierResult> {
  const items = load();
  const payload = buildApiPayload(draft);
  const response = await api.post<UpsertSupplierApiResponse>("/api/v1/suppliers", payload);
  const created = response.data?.data;
  if (!created || created.id == null) {
    throw new Error("Invalid create supplier response.");
  }
  const record = mapApiRecord(created);
  const idx = items.findIndex((s) => s.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.unshift(record);
  save(items);
  return { record, message: response.data?.message || "Supplier created successfully." };
}

export async function updateSupplier(id: ID, draft: SupplierDraft): Promise<UpsertSupplierResult | null> {
  const items = load();
  const idx = items.findIndex((s) => s.id === id);
  if (idx === -1) return null;
  const payload = buildApiPayload(draft);
  const response = await api.put<UpsertSupplierApiResponse>(`/api/v1/suppliers/${id}`, payload);
  const updated = response.data?.data;
  if (!updated) {
    throw new Error("Invalid update supplier response.");
  }
  const record = mapApiRecord(updated);
  items[idx] = record;
  save(items);
  return { record, message: response.data?.message || "Supplier updated successfully." };
}

export async function deleteSupplier(id: ID): Promise<DeleteSupplierResult> {
  const response = await api.delete<DeleteSupplierApiResponse>(`/api/v1/suppliers/${id}`);
  const items = load();
  const next = items.filter((s) => s.id !== id);
  const removed = next.length !== items.length;
  if (removed) save(next);
  return { success: removed, message: response.data?.message || "Supplier deleted successfully." };
}

export function recordToSupplierDraft(s: SupplierRecord): SupplierDraft {
  return {
    name: s.name,
    fatherName: s.fatherName ?? "",
    cnic: s.cnic ?? "",
    contactPerson: s.contactPerson ?? "",
    phone: s.phone ?? "",
    email: s.email ?? "",
    country: s.country ?? null,
    state: s.state ?? null,
    city: s.city ?? "",
    postalCode: s.postalCode ?? "",
    addressLine1: s.addressLine1 ?? "",
    addressLine2: s.addressLine2 ?? "",
    type: normalizeSupplierType(s.type),
    status: s.status,
  };
}
